import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import Select from '__components/Inputs/Select';
import { VARIABLE_TYPES } from 'pages/SmartConnectors/constants';
import type {
  FlowLoadErrors,
  GenericOption,
  RunSmartConnectorFlowLoad,
  RunSmartConnectorInputData,
  SmartConnector,
  SmartConnectorFlowLoad,
} from 'pages/SmartConnectors/types';
import {
  getSmartConnectorVariables,
  getVariableTypeLabel,
  isCSVLoadStep,
} from 'pages/SmartConnectors/SmartConnector/helpers';
import { FlowBuilderContext } from 'pages/SmartConnectors/SmartConnector/SmartConnectorBuilder/FlowBuilder/context';
import { Section } from './Section';
import { useFlashTransition } from 'hooks/useFlashState';
import { getIncludedRecordsOptions } from '../helpers';
import { useScope } from 'pages/SmartConnectors/SmartConnector/hooks/useScope';

const errorsOrder: Partial<Record<keyof RunSmartConnectorInputData, number>> = {
  scope: 0,
  variable: 1,
  filter: 2,
};

export const SourceSettings: FC<{
  loadId?: string;
  smartConnectorToRun: SmartConnector;
  inputData: RunSmartConnectorInputData;
  onChange: Dispatch<SetStateAction<RunSmartConnectorFlowLoad>>;
  showError: boolean;
  errors: FlowLoadErrors['input_data'];
  resetError: (
    patch:
      | Partial<FlowLoadErrors>
      | ((prev: FlowLoadErrors) => Partial<FlowLoadErrors>)
  ) => void;
}> = ({
  loadId,
  smartConnectorToRun,
  inputData,
  onChange,
  showError = false,
  errors,
  resetError,
}) => {
  const { t } = useTranslation();
  const { smartConnector } = useContext(FlowBuilderContext);

  const { scopeOptions } = useScope(smartConnector, true);

  const scope = inputData.scope;

  const variablesList = useMemo(() => {
    if (!scope) {
      return [];
    }

    return getSmartConnectorVariables({
      execution_variables: (smartConnector.execution_variables || []).filter(
        (variable) => variable.scope === scope
      ),
      flow: {
        additional_variables: [],
        loads: (smartConnector.flow.loads || []).reduce<
          SmartConnectorFlowLoad[]
        >((acc, load) => {
          if (loadId && loadId === load.id) {
            return acc;
          }
          if (
            isCSVLoadStep(load) &&
            load.scope === scope &&
            load.custom_object === smartConnectorToRun.custom_object
          ) {
            acc.push(load);
          }
          if (!isCSVLoadStep(load) && load.return_scopes?.length) {
            const returnScopes = load.return_scopes.filter(
              (returnScope) => returnScope.name === scope
            );
            if (returnScopes.length) {
              acc.push({ ...load, return_scopes: returnScopes });
            }
          }
          return acc;
        }, []),
      },
    }).filter(
      (variable) =>
        variable.data_type === 'uuid' || variable.data_type === 'string'
    );
  }, [smartConnector, smartConnectorToRun, scope, loadId]);

  const variableOptions = useMemo(() => {
    if (!variablesList.length) {
      return [];
    }
    return [
      VARIABLE_TYPES.related_object_upload,
      VARIABLE_TYPES.data_source,
      VARIABLE_TYPES.additional,
    ]
      .map((type) => ({
        label: getVariableTypeLabel(type, t),
        options: variablesList.reduce<GenericOption[]>((acc, variable) => {
          if (variable.type === type) {
            acc.push({
              value: variable.id,
              label: variable.name,
            });
          }
          return acc;
        }, []),
      }))
      .filter((group) => group.options.length);
  }, [variablesList, t]);

  const selectedVariable = variablesList.find(
    (variable) => variable.id === inputData.variable
  );

  const isLoadVariable =
    selectedVariable?.type === VARIABLE_TYPES.related_object_upload;

  const includedRecordsOptions = getIncludedRecordsOptions(t);

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const firstErrorKey = useMemo(() => {
    return (
      Object.keys(errors || {}) as (keyof Exclude<
        FlowLoadErrors['input_data'],
        undefined
      >)[]
    )
      .sort((a, b) => (errorsOrder[a] || 0) - (errorsOrder[b] || 0))
      .find((key) => errors?.[key]);
  }, [errors]);

  const errorToShow =
    showError && firstErrorKey ? errors?.[firstErrorKey] || null : null;

  const wrapperRefs = useRef<
    Record<
      keyof Exclude<FlowLoadErrors['input_data'], undefined>,
      HTMLDivElement | null
    >
  >({
    scope: null,
    variable: null,
    filter: null,
  });

  useEffect(() => {
    if (errorToShow) {
      flashErrorMessage(errorToShow);
      wrapperRefs.current[firstErrorKey!]?.scrollIntoView({
        behavior: 'smooth',
      });
    }
    return () => cancelFlash();
  }, [errorToShow, flashErrorMessage, cancelFlash, firstErrorKey]);

  const validate = useMemo(
    () => ({
      message,
      showMessage,
    }),
    [message, showMessage]
  );

  const setInputData = useCallback(
    (patch: Partial<RunSmartConnectorInputData>) =>
      onChange((prev) => ({
        ...prev,
        input_data: { ...prev.input_data, ...patch },
      })),
    [onChange]
  );

  const handleChangeScope = useCallback(
    ({ value }: GenericOption) => {
      setInputData({ scope: value, variable: undefined, filter: undefined });
      resetError({ input_data: undefined });
    },
    [setInputData, resetError]
  );

  const handleChangeVariable = useCallback(
    ({ value }: GenericOption) => {
      setInputData({ variable: value, filter: undefined });
      resetError((prev) => ({
        ...prev,
        input_data: {
          ...prev.input_data,
          variable: undefined,
          filter: undefined,
        },
      }));
    },
    [setInputData, resetError]
  );

  const handleChangeFilter = useCallback(
    ({ value }: GenericOption<RunSmartConnectorInputData['filter'] | ''>) => {
      setInputData({ filter: value || undefined });
      resetError((prev) => ({
        ...prev,
        input_data: {
          ...prev.input_data,
          filter: undefined,
        },
      }));
    },
    [setInputData, resetError]
  );

  return (
    <Section title={t('Source Settings')}>
      <div ref={(el) => (wrapperRefs.current['scope'] = el)}>
        <Select
          label={t('Choose Scope')}
          placeholder={t('Choose Scope')}
          options={scopeOptions}
          value={inputData.scope}
          onChange={handleChangeScope}
          margin
          error={!!errors?.scope}
          validate={firstErrorKey === 'scope' ? validate : undefined}
        />
      </div>
      <div ref={(el) => (wrapperRefs.current['variable'] = el)}>
        <Select
          label={t('Record ID')}
          placeholder={t('Choose Variable')}
          options={variableOptions}
          value={
            selectedVariable && {
              value: selectedVariable.id,
              label: selectedVariable.name,
            }
          }
          onChange={handleChangeVariable}
          margin
          error={!!errors?.variable}
          validate={firstErrorKey === 'variable' ? validate : undefined}
        />
      </div>
      <div ref={(el) => (wrapperRefs.current['filter'] = el)}>
        {isLoadVariable ? (
          <Select
            label={t('Included Records')}
            placeholder={t('Choose Included Records')}
            options={includedRecordsOptions}
            value={inputData.filter || ''}
            onChange={handleChangeFilter}
            margin
            error={!!errors?.filter}
            validate={firstErrorKey === 'filter' ? validate : undefined}
          />
        ) : null}
      </div>
    </Section>
  );
};
